<template>
  <div class="relative" :data-testid="dataTestid">
    <V2AtomsTooltip
      v-if="!isUpdatedInputStates"
      v-model="showError"
      class="absolute -top-10 left-4 !z-[2]"
      type="error"
      tip-x="left"
    >
      {{ errorMessage }}
    </V2AtomsTooltip>

    <ul class="relative" :class="[component.wrapper, listWrapperClass]">
      <li
        v-for="(option, optionIndex) in optionArr"
        :key="optionIndex"
        ref="checkbox_clickable_li"
        class="relative rounded-xl text-inherit"
        :class="[
          component.display,
          component.padding,
          {
            'is-checked': isSelected(option),
            'is-disabled cursor-not-allowed': option && option.disabled,
            'cursor-pointer': option && !option.disabled,
            'align-top': option?.description,
            'items-center': !option?.description,
            '!flex flex-col': option.image && imagePosition === 'top',
            '!p-0 min-w-fit': noBgSelectionBox
          },
        ]"
        @click="handleSelectOption(option)"
      >
        <input
          v-if="!option._isToggle"
          v-bind="$attrs"
          :id="handleId(option.label || option)"
          ref="input"
          :label="
            removeHtmlString(option.label || option)
              .replace(/[\r\n]/gm, '')
              .replace(/\s+/gm, ' ')
              .trim()
          "
          :value="option[dataValue] ?? option"
          :name="handleInputName(option)"
          :type="inputType"
          :checked="isSelected(option)"
          :disabled="option && option.disabled"
          class="peer appearance-none col-start-1 row-start-1"
          :class="[
            {
              hidden: ticker === 'none',
            },
          ]"
        />

        <div
          v-if="!option._isToggle"
          ref="selection-box"
          class="selection-box absolute top-0 left-0 w-full h-full bg-semantic-surface-default"
          :class="[
            'peer-checked:bg-semantic-surface-container-low peer-checked:border peer-checked:border-semantic-primary-default',
            {
              'rounded-xl border border-semantic-outline-variant': !noBox,
              '!border-[#d5d5d5]': !noBox && option && option.disabled,
              'hover:border hover:border-success hover:shadow-md z-10 !bg-transparent peer-checked:shadow-md peer-checked:border peer-checked:border-success peer-checked:!bg-transparent':
                ticker === 'icon',
              '!bg-transparent peer-checked:!bg-transparent !border-none': noBgSelectionBox,
              'border-semantic-states-error-default': isUpdatedInputStates && showError && !noBox
            },
          ]"
        />

        <div
          v-if="option._isToggle"
          ref="toggle-box"
          class="absolute top-0 left-0 w-full h-full bg-semantic-surface-default rounded-xl border border-semantic-outline-variant"
        />

        <template v-if="type !== 'chip' && ticker !== 'none' && !option._isToggle">
          <div
            v-if="
              ['square', 'circle'].includes(ticker) &&
                (isSelected(option) ||
                  (Array.isArray(currValue) &&
                    currValue.includes(optionIndex + 1)) ||
                  currValue === optionIndex + 1)
            "
            ref="ticker"
            class="flex items-center justify-start"
            :class="[
              option.image ? 'absolute top-2 left-2 z-[3]' : 'relative col-start-1 col-end-3 row-start-1 w-full',
              !option.image && component.position.box,
              {
                '!place-self-start': option && option.description && !option.image,
              },
            ]"
          >
            <V2IconsCheckSquare
              v-if="ticker === 'square'"
              :fill="semanticColorLookup('primary.default')"
              :style="{ width: iconSize, height: iconSize }"
            />
            <V2IconsCheckCircle
              v-else-if="ticker === 'circle'"
              :style="{ width: iconSize, height: iconSize }"
            />
          </div>

          <div
            v-else
            ref="ticker"
            class="border border-gray-400"
            :class="[
              option.image ? 'absolute top-2 left-2 z-[3]' : 'relative',
              !option.image && component.position.box,
              component.ticker,
              component.tickerSize,
              {
                '!border-gray-350 !bg-gray-350': option && option.disabled,
                '!min-h-10 !min-w-10 before:!bg-none peer-checked:!bg-transparent':
                  ticker === 'icon',
                '!place-self-start': option && option.description && !option.image,
                'border-semantic-states-error-default': isUpdatedInputStates && showError && !noBox
              },
            ]"
          >
            <img
              v-if="ticker === 'icon' && option.tickerIcon"
              :src="`/icons/${option.tickerIcon}.svg`"
              :alt="option.label || option"
              width="40"
              height="40"
            />
          </div>
        </template>

        <div
          v-if="option.image && imagePosition === 'top'"
          class="relative flex justify-center items-center mb-2"
          :class="[
            component.position.label,
            option.imageWrapperClass,
            {
              'col-start-1 col-end-3 row-start-1': direction === 'row' && ticker !== 'none',
            },
          ]"
        >
          <img
            :src="option.image"
            :alt="option.label || option"
            :width="option.imageWidth || 40"
            :height="option.imageHeight || 40"
            :class="[
              'object-contain',
              option.imageClass,
            ]"
          />
        </div>

        <div
          v-if="option.image && imagePosition === 'side'"
          class="flex justify-center items-center z-[2] relative"
          :class="[
            component.position.icon,
            {
              '!place-self-start': option && option.description,
            },
          ]"
        >
          <img
            :src="option.image"
            :alt="option.label || option"
            :width="option.imageWidth || 40"
            :height="option.imageHeight || 40"
            class="object-contain"
          />
        </div>

        <div
          ref="label"
          class="flex-row grow z-[1] text-semantic-surface-on-surface peer-checked:text-semantic-surface-on-surface peer-checked:font-bold mr-1"
          :class="[
            component.position.label,
            handleIconMargin(option),
            option && (option.icon || option.info) && !option.image ? '' : '',
            {
              'self-center': (option.icon || (option.image && imagePosition === 'side')) && !(option.image && imagePosition === 'top'),
              '!text-[#a3a4a8]': option && option.disabled,
              'text-base': size === 'base',
              'text-sm': size === 'small',
              'text-xs': size === 'extraSmall',
              'font-bold': option?.description,
              'text-center !mr-0': option.image && imagePosition === 'top',
            },
          ]"
        >
          <!-- eslint-disable vue/no-v-html -->
          <p v-html="option?.label ?? option">
          </p>

          <p
            v-if="option?.description"
            class="break-words font-normal"
            :class="{
              'text-sm': size === 'base',
              'text-xs': size === 'small',
              'text-2xs': size === 'extraSmall',
            }"
            v-html="option?.description"
          >
          </p>
        </div>

        <div
          class="tooltip flex items-center"
          :class="[
            infoIconPosition === 'absolute-top-right' ? 'absolute' : 'relative',
            component.position.icon,
            direction === 'col' ? 'w-full justify-center' : '',
            {
              '!place-self-start': option && option.description,
            },
          ]"
        >
          <Component
            :is="option.icon"
            v-if="option.icon"
            ref="icon"
            width="40"
            height="40"
          />

          <V2MoleculesPopover
            v-if="!_isEmpty(option && option.info)"
            type="info"
            size="large"
            class="relative"
            :action-type="infoShowAction"
            :popover-class="infoTooltipClass"
            :group-key="popoverGroupKey"
            :class="[
              {
                '-mt-1 mr-2': infoIconPosition === 'absolute-top-right',
              },
              direction === 'col' && infoIconPosition === 'default' ? 'flex w-full justify-center' : ''
            ]"
          >
            <template #default>
              <V2IconsInfoCircle
                ref="info-icon"
                type="qs-mark-outline"
                class="z-[3]"
                :fill="option.info?.infoIconFill || semanticColorLookup('primary.default')"
                :width="infoIconSizeValue.width"
                :height="infoIconSizeValue.height"
              />
            </template>
            <template v-if="option.info && option.info.title" #title>
              {{ option.info.title }}
            </template>
            <template v-if="option.info.content" #description>
              <p
                :class="[
                  infoTooltipTextClass,
                  {
                    'mt-2': !_isEmpty(option.info.title)
                  }
                ]"
                v-html="option.info.content"
              ></p>
            </template>
          </V2MoleculesPopover>
        </div>
      </li>
    </ul>

    <V2AtomsButton
      v-if="listLimit > 0 && toggleStyle === 'external'"
      size="medium"
      theme="tertiary"
      class="hover:!text-dark mx-auto mt-4"
      :full="false"
      :enable-tracking="false"
      @click="toggleShowMore"
    >
      View {{ showMore ? "less" : "more" }}

      <V2IconsCaret class="ml-3" :direction="showMore ? 'top' : 'bottom'" />
    </V2AtomsButton>

    <div
      v-if="showError && isUpdatedInputStates"
      class="text-semantic-states-error-default text-sm pt-2"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'
import { useId } from 'vue'

defineOptions({
  name: 'V2AtomsSelection'
})

const emit = defineEmits(['update', 'toggle'])

const props = defineProps({
  // stable hook for UI test targeting — see docs/data-testid.md
  dataTestid: {
    type: String,
    default: ''
  },

  // for radio buttons
  name: {
    type: String,
    default: ''
  },

  size: {
    type: String,
    default: 'base',
    validator: value => ['extraSmall', 'small', 'base'].includes(value)
  },

  type: {
    type: String,
    default: 'checkbox',
    validator: value => ['checkbox', 'radio', 'chip'].includes(value)
  },

  ticker: {
    type: String,
    default: 'square',
    validator: value => ['circle', 'square', 'icon', 'none'].includes(value)
  },

  tickerSize: {
    type: String,
    default: 'default',
    validator: value => ['small', 'default'].includes(value)
  },

  direction: {
    type: String,
    default: 'row',
    validator: value => ['col', 'row'].includes(value)
  },

  options: {
    type: Array,
    default: () => [
      {
        value: 1,
        label: 'one',
        description: 'Cheap basic cover to meet my visa requirements',
        name: 'one',
        info: {
          title: 'one',
          content: 'one one one'
        },
        disabled: false
      },
      {
        value: 2,
        label: 'two',
        name: 'two',
        icon: 'IconsInfoCircle',
        image: '/path/to/image.png', // Optional: image URL (use imagePosition prop to control placement)
        imageWidth: 40, // Optional: image width (default: 40)
        imageHeight: 40 // Optional: image height (default: 40)
      }
    ]
  },

  dataValue: {
    type: String,
    default: 'value'
  },

  inline: {
    type: Boolean,
    default: false
  },

  switchIcon: {
    type: Boolean,
    default: false
  },

  cols: {
    type: Number,
    default: 1
  },

  mobileCols: {
    type: Number,
    default: 1
  },

  errorMessage: {
    type: String,
    default: ''
  },

  infoShowMultiple: {
    type: Boolean,
    default: false
  },

  infoShowAction: {
    type: String,
    default: 'click',
    validator: value => ['click', 'hover'].includes(value)
  },

  noBox: {
    type: Boolean,
    default: false
  },

  errorTooltip: {
    type: Boolean,
    default: true
  },

  noBoxGap: {
    type: String,
    default: ''
  },

  tickerPosition: {
    type: String,
    default: 'middle',
    validator: value => ['middle', 'top', 'bottom'].includes(value)
  },

  checkValueError: {
    type: Boolean,
    default: true
  },

  listWrapperClass: {
    type: String,
    default: ''
  },

  // if listLimit is more than one it show view more
  listLimit: {
    type: Number,
    default: 0
  },
  // checkbox type only: 0 = unlimited, otherwise blocks selecting past this count
  maxSelect: {
    type: Number,
    default: 0
  },
  returnFullObject: {
    type: Boolean,
    default: false
  },

  imagePosition: {
    type: String,
    default: 'top',
    validator: value => ['top', 'side'].includes(value)
  },

  infoIconPosition: {
    type: String,
    default: 'default',
    validator: value => ['default', 'absolute-top-right'].includes(value)
  },

  infoIconSize: {
    type: String,
    default: 'default',
    validator: value => ['default', 'small'].includes(value)
  },

  infoTooltipClass: {
    type: String,
    default: ''
  },

  infoTooltipTextClass: {
    type: String,
    default: ''
  },

  // Toggle button style: 'external' (default, below list) or 'inline' (inside list as item)
  toggleStyle: {
    type: String,
    default: 'external',
    validator: value => ['external', 'inline'].includes(value)
  },

  // Custom labels/icons for inline toggle buttons
  toggleMoreLabel: {
    type: String,
    default: 'View More'
  },

  toggleLessLabel: {
    type: String,
    default: 'View Less'
  },

  toggleMoreIcon: {
    type: String,
    default: '/icons/health-insurance/funnel/Plus-Regular.svg'
  },

  toggleLessIcon: {
    type: String,
    default: '/icons/health-insurance/funnel/Minus-Regular.svg'
  },

  toggleIconWidth: {
    type: Number,
    default: 40
  },

  toggleIconHeight: {
    type: Number,
    default: 40
  },

  noBgSelectionBox: {
    type: Boolean,
    default: false
  },

  dataTestid: {
    type: String,
    default: ''
  },

  isUpdatedInputStates: {
    type: Boolean,
    default: false
  }
})

const $attrs = useAttrs()

const _ID = $attrs.id

const currValue = defineModel('modelValue', {
  type: [String, Array, Object,Number],
  default: ''
})

// Emit 'update' event for backward compatibility with MoleculesSelection
watch(() => currValue.value, () => {
  emit('update', currValue.value)
}, { deep: true })

const showError = computed(() => {
  if (!props.errorMessage || !props.errorTooltip) {
    return false
  }

  if (!props.checkValueError) {
    return true
  }

  return Array.isArray(currValue.value) ? _isEmpty(currValue.value) : !currValue.value
})

const inputType = computed(() => props.type === 'chip' ? 'radio': props.type) 

const showMore = ref(false)

// Flip the view-more/less state and let the parent know (true = expanded)
function toggleShowMore () {
  showMore.value = !showMore.value
  emit('toggle', showMore.value)
}

const optionArr = computed(() => {
  if (props.listLimit === 0) {
    return props.options
  }

  const options = JSON.parse(JSON.stringify(props.options))
  const limit = !showMore.value ? props?.listLimit : 0

  // External toggle style (default): show button below list
  if (props.toggleStyle === 'external') {
    return limit ? options.filter((_, index) => limit >= index + 1) : options
  }

  // Inline toggle style: insert toggle button as last item in list
  if (props.toggleStyle === 'inline') {
    if (options.length > props.listLimit) {
      if (showMore.value) {
        // Show all options + "Less" button as last item
        return [
          ...options,
          {
            value: '__toggle_less__',
            label: props.toggleLessLabel,
            image: props.toggleLessIcon,
            imageWidth: props.toggleIconWidth,
            imageHeight: props.toggleIconHeight,
            _isToggle: true,
            info: null // Hide info icon for toggle button
          }
        ]
      }

      // Show limited options + "More" button as last item
      return [
        ...options.slice(0, props.listLimit),
        {
          value: '__toggle_more__',
          label: props.toggleMoreLabel,
          image: props.toggleMoreIcon,
          imageWidth: props.toggleIconWidth,
          imageHeight: props.toggleIconHeight,
          _isToggle: true,
          info: null // Hide info icon for toggle button
        }
      ]
    }
    return options
  }

  // Fallback: should never reach here due to toggleStyle validator
  return options
})

const checkboxMap = reactive({})

// Options can arrive asynchronously after mount (e.g. fetched from an API), so
// checkboxMap must be populated reactively rather than once from props.options
// at setup time — otherwise tooltip icons never render for late-arriving options.
// Existing entries (and any open/closed tooltip state) are preserved on each run.
watch(() => props.options, options => {
  if (_isEmpty(options)) {
    return
  }

  options.forEach((option, index) => {
    if (option?.info && !checkboxMap[`options-${index}`]) {
      checkboxMap[`options-${index}`] = {
        show: false,
        tipX: props.switchIcon ? 'left' : 'right',
        tipY: props.switchIcon ? 'top' : 'bottom'
      }
    }
  })
}, { immediate: true })

function getGridColsClass (type) {
  if (type === 'chip') {
    return 'grid-cols-[1rem,auto]'
  }
  return 'grid-cols-[1.5rem,auto]'
}

const component = computed(() => {
  const wrapper = (() => {
    let temp

    if (props.inline) {
      temp = 'flex flex-col sm:flex-row sm:flex-wrap'
    } else {
      temp = `grid grid-cols-${props.mobileCols} md:grid-cols-${props.cols} ${
        props.noBox ? 'sm:gap-2' : 'sm:gap-4'
      }`
    }

    return `gap-2 ${temp}`
  })()

  const display = (() => {
    let style = getKey(props.direction, {
      col: 'justify-items-center',
      row: (() => {
        if (props.ticker === 'icon') {
          return 'grid-cols-[2.5rem,auto]'
        }

        if (props.ticker === 'none') {
          return '!flex items-center justify-start'
        }

        return removeClassExtras(`
          ${props.noBox ? '' : 'min-h-12'}
          ${
            props.switchIcon
              ? 'grid-cols-[auto,1.5rem]'
              : getGridColsClass(props.type)
          }
        `)
      })()
    })

    style += ` grid ${props.inline && 'sm:inline-grid'}`

    return style
  })()

  const tickerPosition = getKey(props.tickerPosition, {
    middle: 'place-self-center',
    top: 'place-self-start',
    bottom: 'place-self-end'
  })

  const position = getKey(props.switchIcon, {
    true: {
      box: props.direction === 'row' ? 'row-start-1' : 'row-start-3',
      label:
        props.direction === 'row' ? 'row-start-1 col-start-1' : 'row-start-2',
      icon: 'row-start-1 col-start-1'
    },
    false: {
      box:
        props.direction === 'row'
          ? `row-start-1 col-start-1 ${tickerPosition}`
          : '',
      label:
        props.direction === 'row' && props.ticker !== 'none' 
          ? 'row-start-1 col-start-2'
          : '',
      icon:
        props.direction === 'row' && props.ticker !== 'none'
          ? 'col-start-1 col-end-3 row-start-1 justify-end self-center w-full'
          : ''
    }
  })

  const chipRow = 'p-3'
  
  const padding = getKey(props.direction, {
    row: props.noBox ? '' : chipRow,
    col: 'p-4 min-w-[9.25rem]'
  })

  const ticker = getKey(props.ticker, {
    circle: props.tickerSize === 'small' ? 'rounded-[.125rem] before:rounded-[.125rem] border-semantic-primary-default' : 'rounded-full before:rounded-full',
    square: props.tickerSize === 'small' ? 'rounded-[.125rem] before:rounded-[.125rem] border-semantic-primary-default' : 'rounded-[.25rem] before:rounded-[0.25rem]',
    icon: '!border-none'
  })

  const tickerSize = (() => {
    const size = (() => {
      if (props.tickerSize === 'small') {
        return '0.75rem' // 12px
      }

      if (props.noBox) {
        return '1.375rem'
      }

      return '1.25rem'
    })()

    const box = getKey(size, {
      '0.75rem': 'exact-wh-[0.75rem]',
      '1.25rem': 'exact-wh-[1.25rem]',
      '1.375rem': 'exact-wh-[1.375rem]'
    })

    const check = getKey(size, {
      '0.75rem': 'before:w-[0.75rem] before:h-[0.75rem]',
      '1.25rem': 'before:w-[1.25rem] before:h-[1.25rem]',
      '1.375rem': 'before:w-[1.375rem] before:h-[1.375rem]'
    })

    return `${box} ${check}`
  })()

  return {
    wrapper: removeClassExtras(wrapper),
    display: removeClassExtras(display),
    position,
    padding,
    ticker,
    tickerSize
  }
})

const iconSize = computed(() => {
  if (props.tickerSize === 'small') {
    return '12px' // 0.75rem
  }
  return '20px' // 1.25rem (default)
})

const selectionPopoverGroupUid = useId()

const infoIconSizeValue = computed(() => {
  if (props.infoIconSize === 'small') {
    return { width: '16px', height: '16px' }
  }
  return { width: '24px', height: '24px' } // default
})

const popoverGroupKey = computed(() => {
  if (!props.infoShowMultiple && props.infoShowAction === 'click') {
    return `selection-${_ID || selectionPopoverGroupUid}`
  }

  return ''
})

function handleId (label) {
  if (!label) {
    return
  }

  const newId = removeHtmlString(label)
    ? removeHtmlString(label)
      .replace(/[\r\n]/gm, '')
      .replace(/\s+/gm, ' ')
      .trim()
    : ''

  return _ID
    ? strToKebabCase(`${_ID}${newId}`)
    : strToKebabCase(newId)
}

function handleInputName (option) {
  if (props.type === 'radio') {
    if (!props.name) {
      // eslint-disable-next-line no-console
      console.error('Name prop is required for radio type.')
      return
    }

    return props.name
  }

  return removeHtmlString(option?.name || option?.label || option)
    .replace(/[\r\n]/gm, '')
    .replace(/\s+/gm, ' ')
    .trim()
}

function handleSelectOption (option) {
  try {
    if (option.disabled) {
      return
    }

    // Handle inline toggle button clicks
    // Toggle buttons should not be selectable, return early to prevent selection
    if (option._isToggle) {
      const value = option[props.dataValue] ?? option
      if (value === '__toggle_more__' || value === '__toggle_less__') {
        toggleShowMore()
      }
      // Early return to prevent toggle buttons from being added to selection
      return
    }

    if (props.type === 'checkbox') {
      // Copy rather than reuse currValue.value directly -- reassigning the exact same
      // array reference back to a ref is a no-op as far as Vue's change detection is
      // concerned, so every selection after the first would silently fail to emit.
      const currentArray = Array.isArray(currValue.value) ? [...currValue.value] : []

      const findIndex = currentArray.indexOf(
        option[props.dataValue] ?? option
      )

      if (findIndex === -1) {
        if (props.maxSelect > 0 && currentArray.length >= props.maxSelect) {
          return
        }

        currentArray.push(props.returnFullObject ? option : (option[props.dataValue] ?? option))
      } else {
        currentArray.splice(findIndex, 1)
      }

      currValue.value = currentArray
    } else {
      currValue.value = props.returnFullObject ? option : (option[props.dataValue] ?? option)
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
  }
}

function isSelected (option) {
  // Toggle buttons should never be selected
  if (option._isToggle) {
    return false
  }

  const value = option[props.dataValue] ?? option

  if (props.type === 'radio' || props.type === 'chip') {
    if (typeof currValue.value === 'object' && currValue.value !== null) {
      return `${currValue.value[props.dataValue]}`.toLowerCase() === `${value}`.toLowerCase()
    }
    return `${currValue.value}`.toLowerCase() === `${value}`.toLowerCase()
  }

  if (props.type === 'checkbox') {
    if (_isEmpty(currValue.value)) {
      return false
    }

    const currList = currValue.value.map(item => item?.[props.dataValue] ?? item)

    return currList.includes(value)
  }
}

function handleIconMargin (option) {
  let row = determineRowMargin(option)

  if (props.noBox) {
    row = props.noBoxGap || 'mx-[0.625rem]'
  }

  if (props.ticker === 'none') {
    row = 'mx-0'
  }

  return getKey(props.direction, {
    row,
    col: determineColMargin(option)
  })
}

function determineRowMargin (option) {
  if (option.image && props.imagePosition === 'top') {
    return 'mx-0'
  }
  if (option.image && props.imagePosition === 'side') {
    return handleIconMarginForOption()
  }
  if (option && option.info && !option.icon) {
    return handleInfoMargin()
  }

  if (option && !option.info && option.icon) {
    return handleIconMarginForOption()
  }

  return 'mx-2'
}

function handleInfoMargin () {
  if (props.switchIcon) {
    return 'ml-9 mr-4'
  } else if (props.type === 'chip') {
    return 'mr-9'
  } 
  return 'ml-2 mr-9'
  
}

function handleIconMarginForOption () {
  if (props.switchIcon) {
    return 'ml-[3.4375rem] mr-4'
  } else if (props.type === 'chip') {
    return 'mr-[3.4375rem]'
  } 
  return 'ml-2 mr-[3.4375rem]'
  
}

function determineColMargin (option) {
  return option && (option.info || option.icon) ? 'mt-4 mb-2' : 'mt-2'
}
</script>